/* eslint-disable no-nested-ternary */
import * as React from 'react';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import { TextField, useTheme } from '@mui/material';

import './index.scss';
import moment from 'moment';
import { Label } from '../custom-typography';

interface PrimaryDatePickerProps {
  value: any;
  onChange:(value: any) => void;
  placeholder?:string;
  minDate?:any;
  maxDate?:any;
  label?:string;
  disabled?: boolean;
  disablePast?: boolean;
}

const PrimaryDatePicker = React.forwardRef(({
  onChange, value, placeholder, label, minDate, maxDate, disabled, disablePast,
}: PrimaryDatePickerProps, _ref) => {
  const theme = useTheme();
  const parsedValue = React.useMemo(() => {
    if (!value) {
      return null;
    }

    if (moment.isMoment(value)) {
      return value.toDate();
    }

    const momentValue = moment(value);
    return momentValue.isValid() ? momentValue.toDate() : null;
  }, [value]);

  const parsedMinDate = React.useMemo(() => {
    if (!minDate) {
      return undefined;
    }

    if (moment.isMoment(minDate)) {
      return minDate.toDate();
    }

    const momentMinDate = moment(minDate);
    return momentMinDate.isValid() ? momentMinDate.toDate() : undefined;
  }, [minDate]);

  const parsedMaxDate = React.useMemo(() => {
    if (maxDate) {
      if (moment.isMoment(maxDate)) {
        return maxDate.toDate();
      }

      const momentMaxDate = moment(maxDate);
      return momentMaxDate.isValid() ? momentMaxDate.toDate() : undefined;
    }

    if (parsedValue) {
      return moment(parsedValue).add(100, 'years').toDate();
    }

    return undefined;
  }, [maxDate, parsedValue]);

  const style = React.useMemo(() => ({
    outline: 'none',
    '& .MuiOutlinedInput-input': {
      padding: '8.5px 14px',
      borderRadius: '2px',
    },
    '&.Mui-focused': {
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
    },
    '&.Mui-focused .MuiOutlinedInput-input': {
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: '4px',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.grey.A400,
        borderRadius: '4px',
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
        borderWidth: '1px',
      },
      '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.grey.A400,
        borderWidth: '1px',
      },
    },
  }), [theme]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      {label && <Label>{label}</Label>}
      <MobileDatePicker
        value={parsedValue}
        inputFormat="MM/dd/yyyy"
        onChange={onChange}
        minDate={parsedMinDate}
        maxDate={parsedMaxDate}
        disabled={disabled}
        disablePast={disablePast}
        renderInput={(params) => (
          <TextField
            sx={style}
            {...params}
            placeholder={placeholder}
            fullWidth
            error={typeof value !== 'string'}
          />
        )}
      />
    </LocalizationProvider>
  );
});

PrimaryDatePicker.defaultProps = {
  placeholder: '',
  label: '',
  minDate: false,
  maxDate: false,
  disabled: false,
  disablePast: true,
};
export default PrimaryDatePicker;
